
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { hasClientProfile, hasPractitionerProfile, isAdmin } from '@/utils/auth-helpers';

interface ProtectedRouteProps {
  element: JSX.Element;
  requiredRole: 'client' | 'practitioner' | 'admin' | null;
}

const ProtectedRoute = ({ element, requiredRole }: ProtectedRouteProps) => {
  const { user, isLoading, activeRole, roleReady, setActiveRole } = useAuth();
  const [roleSwitching, setRoleSwitching] = useState(false);
  const [roleSwitchDone, setRoleSwitchDone] = useState(false);

  const recoveryContext = (() => {
    try {
      const raw = sessionStorage.getItem('password_recovery_in_progress');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts?: number; userType?: string };
      if (!parsed.ts || Date.now() - parsed.ts >= 10 * 60 * 1000) return null;
      return {
        userType: parsed.userType === 'practitioner' ? 'practitioner' : 'client',
      } as const;
    } catch {
      return null;
    }
  })();

  // Auto-switch role for multi-role users
  const needsRoleSwitch = !!user && roleReady && !!requiredRole && activeRole !== requiredRole && !roleSwitching && !roleSwitchDone;

  useEffect(() => {
    if (!needsRoleSwitch || !user || !requiredRole) return;

    let cancelled = false;
    setRoleSwitching(true);

    const checkAndSwitch = async () => {
      let hasRole = false;
      switch (requiredRole) {
        case 'admin':
          hasRole = await isAdmin(user.id);
          break;
        case 'practitioner':
          hasRole = await hasPractitionerProfile(user.id);
          break;
        case 'client':
          hasRole = await hasClientProfile(user.id);
          break;
      }

      if (cancelled) return;

      if (hasRole) {
        console.log(`ProtectedRoute: Auto-switching role from ${activeRole} to ${requiredRole}`);
        setActiveRole(requiredRole);
      }
      setRoleSwitchDone(true);
      setRoleSwitching(false);
    };

    checkAndSwitch();
    return () => { cancelled = true; };
  }, [needsRoleSwitch, user, requiredRole, activeRole, setActiveRole]);

  // Reset switch state when route/role changes
  useEffect(() => {
    setRoleSwitchDone(false);
    setRoleSwitching(false);
  }, [requiredRole]);

  if (recoveryContext) {
    return <Navigate to={`/reset-password?type=${recoveryContext.userType}`} replace />;
  }
  
  if (isLoading || roleSwitching) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }
  
  if (!user) {
    let redirectPath = '/';
    if (requiredRole === 'client') redirectPath = '/client-auth';
    else if (requiredRole === 'practitioner') redirectPath = '/practitioner-auth';
    else if (requiredRole === 'admin') redirectPath = '/admin';
    
    const currentPath = window.location.pathname;
    
    if (!roleReady) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      );
    }
    
    if (currentPath !== '/booking-success' && 
        !currentPath.includes('auth') && 
        currentPath !== '/') {
      console.log(`ProtectedRoute: Redirecting unauthenticated user from ${currentPath} to ${redirectPath}`);
      return <Navigate to={redirectPath} replace />;
    }
    
    if (currentPath === '/booking-success') {
      return <Navigate to="/" replace />;
    }
    
    return <Navigate to={redirectPath} replace />;
  }
  
  if (requiredRole === 'admin' && activeRole !== 'admin') {
    if (!roleSwitchDone) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      );
    }
    return <Navigate to="/admin" replace />;
  }

  // Enforce role-specific access for client and practitioner
  if (requiredRole && requiredRole !== 'admin' && activeRole !== requiredRole) {
    if (!roleReady || !roleSwitchDone) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      );
    }
    
    if (activeRole === null) {
      const authPath = requiredRole === 'practitioner' ? '/practitioner-auth' : '/client-auth';
      console.log(`ProtectedRoute: No active role found, redirecting to ${authPath}`);
      return <Navigate to={authPath} replace />;
    }
    
    console.log(`ProtectedRoute: Role mismatch - required: ${requiredRole}, active: ${activeRole}`);
    
    // Correct redirect for all role types including admin
    let dashboardPath = '/client-dashboard';
    if (activeRole === 'practitioner') dashboardPath = '/practitioner-dashboard';
    else if (activeRole === 'admin') dashboardPath = '/admin/dashboard';
    
    return <Navigate to={dashboardPath} replace />;
  }
  
  return element;
};

export default ProtectedRoute;
